import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Check } from 'lucide-react';
import { PropertiesIcon } from './MobileNav';

export function PropertyPickerModal({ title, subtitle, properties, selectedIds, onToggle, onSave, saving, onClose, emptyText }) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = q
    ? properties.filter((p) => (p.name || '').toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q))
    : properties;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="property-picker-modal"
      >
        <div className="bg-gradient-to-r from-emerald-500 to-blue-500 p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <PropertiesIcon size={32} className="text-white" strokeWidth={1.8} />
          </div>
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          {subtitle && <p className="text-white/80 text-sm mt-1">{subtitle}</p>}
        </div>

        {properties.length > 0 && (
          <div className="px-4 pt-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search properties by name or address..."
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="picker-search-input"
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {properties.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <PropertiesIcon size={48} className="mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
              <p>{emptyText || 'No properties available.'}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Search size={40} className="mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
              <p className="text-sm">No properties match "{search}"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((property) => {
                const isSel = selectedIds.has(property.id);
                return (
                  <button
                    key={property.id}
                    onClick={() => onToggle(property.id)}
                    className={`w-full p-4 rounded-xl border-2 transition-colors flex items-center justify-between ${
                      isSel ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`picker-property-${property.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSel ? 'bg-emerald-500' : 'bg-slate-100'}`}>
                        <PropertiesIcon size={20} className={isSel ? 'text-white' : 'text-slate-400'} strokeWidth={1.8} />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">{property.name}</p>
                        {property.address && <p className="text-xs text-slate-500">{property.address}</p>}
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSel ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                      {isSel && <Check size={14} className="text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 text-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-emerald-500 to-blue-500 text-white hover:opacity-90 disabled:opacity-50"
            data-testid="picker-save-button"
          >
            {saving ? 'Saving...' : `Save (${selectedIds.size} selected)`}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
